import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Clock, ChevronDown, Building2 } from 'lucide-react';
import { WORKFLOW_STEPS } from '@/utils/workflowSteps';
import { STATUS_CFG, PLAN_STATUS, getWfColor } from '@/utils/statusConfig';

const EstablishmentRow = ({ company, plans, isRTL, isAdmin, compTxMap, smartFilter }) => {
  const [expanded, setExpanded] = useState(false);

  // Filter plans based on smartFilter
  const filteredPlans = (() => {
    if (!plans || smartFilter === 'all') return plans;
    if (smartFilter === 'executing') return plans.map(p => ({
      ...p, steps: (p.steps || []).filter(s => s.status === 'executing' || s.status === 'awaiting_approval')
    })).filter(p => p.steps.length > 0);
    if (smartFilter === 'awaiting_approval') return plans.map(p => ({
      ...p, steps: (p.steps || []).filter(s => s.status === 'awaiting_approval')
    })).filter(p => p.steps.length > 0);
    if (smartFilter === 'completed') return plans.map(p => ({
      ...p, steps: (p.steps || []).filter(s => s.status === 'completed')
    })).filter(p => p.steps.length > 0);
    return plans;
  })();

  const allSteps = filteredPlans.flatMap(p => p.steps || []);
  const totalSteps = allSteps.length;
  const doneSteps = allSteps.filter(s => s.status === 'completed' || s.status === 'executing').length;

  const primaryType = plans[0]?.steps?.[0]?.main_type || '';
  const rowColor = getWfColor(primaryType);

  return (
    <div className={`border-b-2 border-b-white last:border-b-0 relative rounded-lg mx-1 my-0.5 border ${rowColor.border} ${rowColor.bg}`} data-testid={`tracking-row-est-${company.company_id}`}>
      <div className={`absolute top-0 bottom-0 w-1.5 ${rowColor.bar} rounded-full`} style={{ insetInlineStart: 0 }} />
      <div className="flex items-center gap-3 px-4 py-2.5 hover:brightness-95 transition-all cursor-pointer"
        onClick={() => setExpanded(!expanded)}>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
        <div className="flex items-center gap-2 min-w-[160px] shrink-0">
          <div className="w-7 h-7 rounded-full flex items-center justify-center bg-indigo-600 shrink-0">
            <Building2 className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-800 truncate leading-tight">{company.company_name}</p>
            <p className="text-[10px] text-indigo-500 truncate leading-tight">{isRTL ? 'منشأة' : 'Establishment'}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-1 min-w-0 flex-wrap">
          {plans.map((plan, i) => {
            const mt = plan.steps?.[0]?.main_type || '';
            const wfc = getWfColor(mt);
            return (
              <span key={i} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 border ${wfc.bg} ${wfc.text} ${wfc.border}`}>
                {isRTL ? (WORKFLOW_STEPS[mt]?.ar || plan.company_name) : (WORKFLOW_STEPS[mt]?.en || plan.company_name)}
              </span>
            );
          })}
        </div>
        <div className="flex items-center gap-0.5 shrink-0" style={{ direction: 'ltr' }}>
          {allSteps.map((s, i) => {
            const tx = compTxMap[s.transaction_id];
            const st = tx?.status || s.status;
            const clr = { planned: 'bg-blue-200', done_unpaid: 'bg-purple-500', executing: 'bg-amber-500', pending: 'bg-amber-500', completed: 'bg-emerald-500', waiting_approval: 'bg-blue-500', cancelled: 'bg-red-400' }[st] || 'bg-blue-200';
            return <div key={i} className={`w-2 h-2 rounded-sm ${clr}`} />;
          })}
        </div>
        <div className="flex items-center gap-1 shrink-0" style={{ direction: 'ltr' }}>
          <div className="w-12 h-1 bg-indigo-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-indigo-500" style={{ width: `${totalSteps > 0 ? Math.round((doneSteps/totalSteps)*100) : 0}%` }} />
          </div>
          <span className="text-[10px] text-indigo-400 tabular-nums">{doneSteps}/{totalSteps}</span>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-3 pt-1 space-y-3">
          {filteredPlans.map((plan, pIdx) => {
            const mt = plan.steps?.[0]?.main_type || '';
            const wfc = getWfColor(mt);
            return (
              <div key={pIdx}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <div className={`w-1.5 h-5 rounded-full ${wfc.bar}`} />
                    <p className={`text-xs font-bold ${wfc.text}`}>
                      {isRTL ? (WORKFLOW_STEPS[mt]?.ar || plan.company_name) : (WORKFLOW_STEPS[mt]?.en || plan.company_name)}
                    </p>
                    <span className="text-[10px] text-slate-400">({plan.total_price?.toLocaleString()} {isRTL ? 'درهم' : 'AED'})</span>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-4 text-[9px] font-semibold text-slate-400">
                      <span className="text-purple-400">{isRTL ? 'السعر' : 'Price'}</span>
                      <span className="text-amber-400">{isRTL ? 'المدفوع' : 'Paid'}</span>
                    </div>
                  )}
                </div>
                <div className={`rounded-lg border ${wfc.border} overflow-hidden`}>
                  {plan.steps?.map((step, idx) => {
                    const tx = step.transaction_id ? compTxMap[step.transaction_id] : null;
                    const txStatus = tx?.status || '';
                    const isActive = step.status === 'executing' || step.status === 'completed';
                    const ps = PLAN_STATUS[step.status] || PLAN_STATUS.planned;
                    return (
                      <div key={idx} className={`flex items-center gap-3 px-3 py-2 ${idx < plan.steps.length - 1 ? `border-b ${wfc.border}` : ''} ${step.status === 'executing' ? 'bg-amber-50/30' : ''} ${step.status === 'done_unpaid' ? 'bg-purple-50/30' : ''}`}>
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                          txStatus === 'completed' ? 'bg-emerald-500' : txStatus === 'waiting_approval' ? 'bg-blue-500' : txStatus === 'pending' ? 'bg-amber-500' : ps.bg
                        }`}>
                          {(txStatus === 'completed' || step.status === 'completed') ? <CheckCircle2 className="w-3 h-3 text-white" /> :
                           step.status === 'done_unpaid' ? <CheckCircle2 className="w-3 h-3 text-white" /> :
                           (step.status === 'executing' || txStatus === 'pending' || txStatus === 'waiting_approval') ? <Clock className="w-3 h-3 text-white" /> :
                           <Building2 className="w-3 h-3 text-slate-500" />}
                        </div>
                        <span className={`text-xs flex-1 min-w-0 ${(txStatus === 'completed' || step.status === 'completed') ? 'text-slate-400 line-through' : 'text-slate-700 font-medium'}`}>
                          {isRTL ? step.label_ar : step.label_en}
                        </span>
                        {isActive && tx && (
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-[10px] text-purple-600 font-semibold" dir="ltr">{tx.price?.toLocaleString() || 0}</span>
                            <span className="text-[10px] text-amber-600 font-semibold w-12 text-center" dir="ltr" data-testid={`comp-paid-${tx.id}`}>{tx.actual_paid_amount != null ? tx.actual_paid_amount.toLocaleString() : '—'}</span>
                          </div>
                        )}
                        {!isActive && (
                          <span className="text-[10px] font-semibold text-slate-500 shrink-0">{step.price?.toLocaleString()} {isRTL ? 'د' : 'AED'}</span>
                        )}
                        {isActive && tx ? (
                          <Badge className={`text-[10px] ${(STATUS_CFG[txStatus] || STATUS_CFG.pending).badge}`} data-testid={`comp-status-${tx.id}`}>
                            {isRTL ? (STATUS_CFG[txStatus] || STATUS_CFG.pending).ar : (STATUS_CFG[txStatus] || STATUS_CFG.pending).en}
                          </Badge>
                        ) : (
                          step.status !== 'planned' ? <Badge className={`text-[10px] border shrink-0 ${ps.badge}`}>{isRTL ? ps.ar : ps.en}</Badge>
                          : <span className="text-[10px] text-slate-400 shrink-0">{isRTL ? 'مخطط — يُنفَّذ من خطط العمل' : 'Planned — manage in Work Plans'}</span>
                        )}
                      </div>
                    );
                  })}
                  <div className={`flex items-center justify-between px-3 py-1.5 ${wfc.bg} border-t ${wfc.border}`}>
                    <span className={`text-[10px] font-semibold ${wfc.text}`}>{isRTL ? 'المجموع' : 'Total'}</span>
                    <span className={`text-xs font-bold ${wfc.text}`}>{plan.total_price?.toLocaleString()} {isRTL ? 'درهم' : 'AED'}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EstablishmentRow;
